import React from "react";
import { motion } from "framer-motion";
import {
  CircleDotDashed,
  CircleCheck,
  CircleX,
  Activity,
  MessageSquare,
  Hash,
  User,
} from "lucide-react";
import tw from "tailwind-styled-components";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const RenderSenderIdStatus = ({ status }) => {
  const statusLower = status.toLowerCase();

  if (statusLower === "pending") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-amber-200">
        <CircleDotDashed className="w-3.5 h-3.5" />
        {"Pending"}
      </div>
    );
  } else if (statusLower === "approved") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
        <CircleCheck className="w-3.5 h-3.5" />
        {"Approved"}
      </div>
    );
  } else if (statusLower === "active") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 ring-1 ring-blue-200">
        <Activity className="w-3.5 h-3.5" />
        {"Active"}
      </div>
    );
  } else {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 ring-1 ring-red-200">
        <CircleX className="w-3.5 h-3.5" />
        {"Rejected"}
      </div>
    );
  }
};

const SenderIdAvatar = ({ senderName }) => {
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "S";
  };

  const getAvatarColor = (name) => {
    const colors = [
      "bg-gradient-to-br from-blue-500 to-blue-600",
      "bg-gradient-to-br from-purple-500 to-purple-600",
      "bg-gradient-to-br from-green-500 to-green-600",
      "bg-gradient-to-br from-orange-500 to-orange-600",
      "bg-gradient-to-br from-pink-500 to-pink-600",
      "bg-gradient-to-br from-indigo-500 to-indigo-600",
    ];
    const index = name ? name.length % colors.length : 0;
    return colors[index];
  };

  return (
    <div
      className={`flex justify-center items-center w-10 h-10 rounded-lg shadow-md ${getAvatarColor( senderName )}`}
    >
      <span className="text-xs font-bold text-white">
        {getInitial(senderName)}
      </span>
    </div>
  );
};



const EmptyState = () => {
  return (
    <motion.div
      className="flex flex-col justify-center items-center px-6 py-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className="relative mb-4">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 rounded-full blur-xl"></div>
        <div className="relative w-12 h-12 bg-gradient-to-br from-[#FF6C6C]/20 to-[#FF6C6C]/10 rounded-full flex items-center justify-center border border-[#FF6C6C]/20">
          <MessageSquare className="w-6 h-6 text-[#FF6C6C]" strokeWidth={1.5} />
        </div>
      </div>

      <div className="space-y-2 text-center">
        <motion.h3
          className="text-base font-semibold text-gray-900"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          No Sender ID Requests
        </motion.h3>
        <motion.p
          className="text-gray-500 text-xs max-w-[200px] leading-relaxed"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          Sender ID requests will appear here when vendors submit them
        </motion.p>
      </div>

      <motion.div
        className="mt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.3 }}
      >
        <div className="flex gap-2 items-center text-xs text-gray-400">
          <div className="w-2 h-2 bg-[#FF6C6C]/30 rounded-full animate-pulse"></div>
          <span>Awaiting requests...</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

const SenderIdSummary = ({ recentSenderIds }) => {
  const navigate = useNavigate()
  const handleSenderIdNavigation = ()=>{
    navigate("/senders")
  }
  return (
    <motion.div
      className="p-5 h-full bg-white rounded-lg shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2 items-center">
          <div className="w-7 h-7 bg-gradient-to-br from-[#FF6C6C]/20 to-[#FF6C6C]/10 rounded-lg flex items-center justify-center">
            <Hash className="w-3.5 h-3.5 text-[#FF6C6C]" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">
            Sender-ID Requests
          </h2>
        </div>
        {/* <button onClick={handleSenderIdNavigation} className="py-1.5 px-3 text-[#FF6C6C] font-semibold text-sm text-center hover:scale-105 transition duration-300 ease-in-out hover:bg-[#FF6C6C]/5 rounded-lg">
          View All
        </button> */}
      </div>

      <div className="space-y-1">
        {!recentSenderIds || recentSenderIds.length === 0 ? (
          <EmptyState />
        ) : (
          recentSenderIds.map((item, index) => {
            const { id, sender, created_at, status, vendor_id } = item;
            return (
              <motion.div
                key={id}
                className="group"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <div className="flex justify-between items-center p-3 rounded-xl border border-transparent transition-all duration-200 hover:bg-gray-50/80 hover:border-gray-100">
                  <div className="flex gap-3 items-center">
                    <SenderIdAvatar senderName={sender} />
                    <div className="flex flex-col">
                      <div className="flex gap-2 items-center">
                        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">
                          {sender.length > 20
                            ? `${sender.substring(0, 20)}...`
                            : sender}
                        </h4>
                        {vendor_id && (
                          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">
                            #{vendor_id}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <User className="w-3 h-3 text-gray-400" />
                        <p className="text-[11px] text-gray-500">
                          {moment(created_at).format("MMM DD, YYYY")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <RenderSenderIdStatus status={status} />
                  </div>
                </div>

                {index !== recentSenderIds.length - 1 && <Seperator />}
              </motion.div>
            );
          })
        )}
      </div>
    </motion.div>
  );
};

const Seperator = tw.div`w-full h-[1px] my-1.5 bg-gray-100`;
export default SenderIdSummary;
